import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { COLLECTION_NAME } from "../../constants/endpoints";

import Table from "../../Components/Table/Table";
import Loader from "../../Components/Loader/Loader";
import { formatDate } from "../../utils";
import PaymentModal2 from "../../Components/PaymentModal2/PaymentModal2";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { Bounce, toast } from "react-toastify";
import { Dropdown, Menu } from "antd";
import {
  REFUND_REQUEST,
  UPDATE_REFUND_REQUEST_STATUS,
} from "../../constants/endpoints";
import ShowMoreText from "../../Components/ShowMoreText/ShowMoreText";
import { useAuth } from "../../context/AuthContext";
import { BsThreeDotsVertical } from "react-icons/bs";

const RefundRequest = () => {
  const [loading, setLoading] = useState(true);
  const [refundRequests, setRefundRequests] = useState([]);
  const [selectedRequesterType, setSelectedRequesterType] = useState("all");
  const [selectedStatusType, setSelectedStatusType] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedRide, setSelectedRide] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { currentUser } = useAuth();

  const normalizeRequesterType = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized.includes("passenger")) return "passenger";
    if (normalized.includes("driver") || normalized.includes("rider")) {
      return "driver";
    }
    return "unknown";
  };

  const toInitialCaps = (value) => {
    const normalized = String(value || "")
      .replaceAll("_", " ")
      .trim()
      .toLowerCase();
    if (!normalized) return "N/A";
    return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  useEffect(() => {
    const fetchRefundRequests = async () => {
      try {
        const snapshot = await getDocs(
          collection(db, COLLECTION_NAME.refundRequests)
        );
        const data = snapshot.docs.map((refundDoc) => {
          const d = refundDoc.data();
          const requesterType = normalizeRequesterType(
            d.requester_type ||
            d.requested_by_role ||
            d.created_by_role ||
            d.user_type ||
            d.created_by
          );
          return {
            doc_id: refundDoc.id,
            id: d.id || refundDoc.id,
            ride_id: d.ride_id || d.rideId || "",
            description: d.description || "",
            reason: d.reasonLabel || d.reason || "",
            total_ride_fare: d.total_ride_fare ?? d.totalRideFare ?? 0,
            created_by: d.created_by || "",
            requester_type: requesterType,
            status: d.status || "",
            created_at: d.created_at?.seconds
              ? new Date(d.created_at.seconds * 1000)
              : null,
          };
        });

        // Keep valid tickets and sort by creation date (include rows with missing fare; modal resolves from ride/payments)
        const filteredData = data
          .filter(
            (item) =>
              Boolean(String(item.ride_id || "").trim()) &&
              ["pending", "in_progress", "completed"].includes(
                String(item.status || "").toLowerCase()
              )
          )
          .sort((a, b) => {
            if (!a.created_at || !b.created_at) return 0;
            return sortOrder === "desc"
              ? b.created_at - a.created_at
              : a.created_at - b.created_at;
          });

        const uniqueRideIds = [
          ...new Set(
            filteredData
              .map((item) => String(item.ride_id || "").trim())
              .filter(Boolean)
          ),
        ];

        const rideIdMap = {};
        await Promise.all(
          uniqueRideIds.map(async (rideId) => {
            try {
              const rideSnap = await getDoc(
                doc(db, COLLECTION_NAME.ridesHistory, rideId)
              );
              if (rideSnap.exists()) {
                const rideData = rideSnap.data();
                rideIdMap[rideId] = {
                  userId: rideData.userId || "",
                  riderId: rideData.riderId || "",
                };
              }
            } catch (error) {
              console.error(`Error fetching ride ${rideId}:`, error);
            }
          })
        );

        const uniqueUserIds = [
          ...new Set(
            filteredData
              .map((item) => {
                const ride = rideIdMap[String(item.ride_id || "").trim()];
                if (!ride) return "";
                if (item.requester_type === "passenger") return ride.userId;
                if (item.requester_type === "driver") return ride.riderId;
                return "";
              })
              .filter(Boolean)
          ),
        ];

        const userEmailMap = {};
        await Promise.all(
          uniqueUserIds.map(async (userId) => {
            try {
              const userSnap = await getDoc(
                doc(db, COLLECTION_NAME.users, userId)
              );
              if (userSnap.exists()) {
                const email = String(userSnap.data().email || "").trim();
                if (email) {
                  userEmailMap[userId] = email;
                }
              }
            } catch (error) {
              console.error(`Error fetching user ${userId}:`, error);
            }
          })
        );

        const enrichedData = filteredData.map((item) => {
          const ride = rideIdMap[String(item.ride_id || "").trim()];
          let customerEmail = "";
          if (ride) {
            const targetUserId =
              item.requester_type === "passenger"
                ? ride.userId
                : item.requester_type === "driver"
                  ? ride.riderId
                  : "";
            customerEmail = userEmailMap[targetUserId] || "";
          }
          return {
            ...item,
            customer_email: customerEmail,
          };
        });

        setRefundRequests(enrichedData);
      } catch (error) {
        console.error("Error fetching refund requests:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRefundRequests();
  }, [sortOrder]);

  const openModal = (ride) => {
    setSelectedRide(ride);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedRide(null);
    setIsModalOpen(false);
  };

  const updateTicketStatus = async (ride, nextStatus, updateNote = "") => {
    const requestId = ride.doc_id || ride.id;
    if (!requestId || !nextStatus) return;

    setUpdatingStatusId(ride.id);
    try {
      const response = await fetch(UPDATE_REFUND_REQUEST_STATUS, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          request_id: requestId,
          status: nextStatus,
          updated_by: currentUser?.email || currentUser?.uid || "admin",
          update_note: updateNote,
        }),
      });

      const rawResponse = await response.text();
      let result = {};
      try {
        result = rawResponse ? JSON.parse(rawResponse) : {};
      } catch (parseError) {
        result = {};
      }

      if (!response.ok) {
        const fallbackMessage =
          rawResponse && rawResponse.trim().startsWith("<!DOCTYPE")
            ? "Status API returned HTML instead of JSON. Please verify backend route PATCH /refund/request/status and base URL."
            : "Failed to update ticket status.";
        const serverMessage =
          result.message ||
          result.error ||
          (rawResponse ? rawResponse.slice(0, 200) : "");
        throw new Error(
          serverMessage
            ? `${fallbackMessage} (HTTP ${response.status}) ${serverMessage}`
            : `${fallbackMessage} (HTTP ${response.status})`
        );
      }

      setRefundRequests((prev) =>
        prev.map((item) =>
          item.id === ride.id ? { ...item, status: nextStatus } : item
        )
      );

      toast.success(result.message || "Ticket status updated successfully.", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
      return true;
    } catch (error) {
      console.error("Error updating refund request status:", error);
      console.error("Status update request context:", {
        endpoint: UPDATE_REFUND_REQUEST_STATUS,
        requestId,
        nextStatus,
        updatedBy: currentUser?.email || currentUser?.uid || "admin",
      });
      toast.error(error.message || "Failed to update ticket status.", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
      return false;
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === "desc" ? "asc" : "desc"));
  };

  const filteredRefundRequests = useMemo(() => {
    let data = refundRequests;

    if (selectedRequesterType !== "all") {
      data = data.filter((item) => item.requester_type === selectedRequesterType);
    }
    if (selectedStatusType !== "all") {
      data = data.filter(
        (item) => String(item.status || "").toLowerCase() === selectedStatusType
      );
    }
    return data;
  }, [refundRequests, selectedRequesterType, selectedStatusType]);

  const columns = useMemo(
    () => [
      { Header: "Ride ID", accessor: "ride_id" },
      {
        Header: "Customer Email",
        accessor: "customer_email",
        Cell: ({ value }) =>
          value ? (
            <a
              href={`mailto:${value}`}
              className="text-blue-600 hover:underline"
            >
              {value}
            </a>
          ) : (
            "N/A"
          ),
      },
      {
        Header: "Description",
        accessor: "description",
        Cell: ({ value }) => <ShowMoreText text={value} />,
      },
      {
        Header: "Reason",
        accessor: "reason",
        Cell: ({ value }) => <ShowMoreText text={value} />,
      },

      {
        Header: "Total Fare",
        accessor: "total_ride_fare",
        Cell: ({ value }) => `$${value}`,
      },
      {
        Header: "Created By",
        accessor: "created_by",
        Cell: ({ value }) => toInitialCaps(value),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => toInitialCaps(value),
      },
      {
        Header: (
          <div
            onClick={toggleSortOrder}
            className="cursor-pointer flex gap-1 items-center"
          >
            Created At{" "}
            {sortOrder === "desc" ? <IoIosArrowDown /> : <IoIosArrowUp />}
          </div>
        ),
        accessor: "created_at",
        Cell: ({ value }) => (value ? formatDate(value) : "N/A"),
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => {
          const ride = row.original;
          const isUpdatingStatus = updatingStatusId === ride.id;
          const normalizedStatus = String(ride.status || "").toLowerCase();
          const isPassengerRequest = ride.requester_type === "passenger";

          const menu = (
            <Menu>
              {isPassengerRequest && normalizedStatus !== "completed" && (
                <Menu.Item
                  key="create-refund"
                  disabled={isUpdatingStatus}
                  onClick={() => openModal(ride)}
                >
                  Create Refund
                </Menu.Item>
              )}
              {normalizedStatus === "pending" && (
                <Menu.Item
                  key="in-progress"
                  disabled={isUpdatingStatus}
                  onClick={() =>
                    updateTicketStatus(
                      ride,
                      "in_progress",
                      "Ticket moved to in_progress by admin"
                    )
                  }
                >
                  Mark In Progress
                </Menu.Item>
              )}
              {normalizedStatus !== "completed" && (
                <Menu.Item
                  key="completed"
                  disabled={isUpdatingStatus}
                  onClick={() =>
                    updateTicketStatus(
                      ride,
                      "completed",
                      "Ticket marked completed by admin"
                    )
                  }
                >
                  Mark Completed
                </Menu.Item>
              )}
            </Menu>
          );

          return (
            <div className="flex items-center justify-center gap-2 w-full">
              <Dropdown overlay={menu} trigger={["click"]}>
                <button
                  disabled={isUpdatingStatus}
                  className={`text-gray-700 hover:text-black text-lg ${isUpdatingStatus ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                >
                  <BsThreeDotsVertical />
                </button>
              </Dropdown>
              {isUpdatingStatus && (
                <span className="text-xs text-gray-600 font-medium animate-pulse">
                  Updating...
                </span>
              )}
            </div>
          );
        },
      },
    ],
    [sortOrder, updatingStatusId]
  );

  const onConfirm = async (
    id,
    ride_id,
    reason = "service_issue",
    totalRideFare,
    amount
  ) => {
    setIsProcessing(true);
    try {
      if (!amount || amount > totalRideFare) {
        toast.error(
          "Refund amount must be less than or equal to total ride fare."
        );
        return;
      }

      const payload = {
        refund_request_id: id,
        ride_id: ride_id,
        refund_reason: reason,
        refund_amount: amount,
      };

      const response = await fetch(REFUND_REQUEST, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        const statusUpdated = await updateTicketStatus(
          selectedRide || {
            id,
            doc_id: selectedRide?.doc_id || id,
          },
          "completed",
          "Refund initiated by admin"
        );

        if (statusUpdated) {
          toast.success(result.message || "Refund initiated successfully", {
            position: "top-right",
            autoClose: 5000,
            transition: Bounce,
          });
        }

        closeModal();
      } else {
        throw new Error(
          result.message || result.error || "Refund failed. Please try again."
        );
      }
    } catch (error) {
      console.error("Refund error:", error);
      toast.error(error.message || "Refund failed. Please try again.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsProcessing(false);
      closeModal();
    }
  };

  return (
    <div className="px-1 sm:px-1 md:px-4 min-h-screen">
      <h1 className="text-2xl md:text-3xl font-bold mt-8 md:mt-5 mb-4 text-primary">
        Customer Support
      </h1>
      <div className="mb-4">
        <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex flex-col md:flex-row md:items-end gap-3 md:gap-4">
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Request Type
            </label>
            <select
              value={selectedRequesterType}
              onChange={(e) => setSelectedRequesterType(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Requests</option>
              <option value="passenger">Passenger</option>
              <option value="driver">Driver</option>
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Status
            </label>
            <select
              value={selectedStatusType}
              onChange={(e) => setSelectedStatusType(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <>
          <Table
            columns={columns}
            data={filteredRefundRequests}
            screen={"refundRequest"}
          />
          <PaymentModal2
            isOpen={isModalOpen}
            onRequestClose={closeModal}
            ride={selectedRide}
            isRefundModal
            processing={isProcessing}
            onConfirm={(amount, opts) => {
              const fareCap =
                opts?.totalRideFareCap ??
                selectedRide?.total_ride_fare ??
                0;
              onConfirm(
                selectedRide?.id,
                selectedRide?.ride_id,
                "service_issue",
                fareCap,
                amount
              );
            }}
          />
        </>
      )}
    </div>
  );
};

export default RefundRequest;

